'use client';

/**
 * CommonCautions — 이벤트 등에서 쓰는 접이식「유의사항」블록
 *
 * [사용 예]
 * ```tsx
 * <CommonCautions
 *   open={false}
 *   title="유의사항"
 *   items={[
 *     `첫째 줄
 * 둘째 줄 (백틱 문자열 줄바꿈 + pre-line)`,
 *     <>줄바꿈이 필요하면 <br /> 를 넣은 JSX</>,
 *     "한 줄 문자열",
 *   ]}
 * />
 * ```
 *
 * [옵션]
 * - `items` : 문구 배열. 개수에 따라 마크업이 달라짐.
 *   - 0개: 본문 없음(제목 줄만).
 *   - 1개: `<p>` 한 덩어리.
 *   - 2개 이상: `<ul><li>` 목록.
 * - `title`: 제목 (기본 `"유의사항"`).
 * - `open`: 처음에 펼칠지 여부 (기본 `true`). `false`면 마운트 시 접힘.
 * - `listStyle`: `false`면 목록 불릿 스타일 제거용 클래스 적용 (기본 `true`).
 * - `itemsClassName`: 본문 `<p>` / `<ul>`에 붙일 추가 클래스.
 * - `className`: 최상위 `<section>`에 붙일 추가 클래스.
 * - `children`: items 외 추가 렌더할 자식 요소, items 없이 사용 가능 (예: 버튼, 안내 블록 등).
 * - `childrenPosition`: `children`의 렌더 위치. `"after"`(기본)면 items 뒤, `"before"`면 items 앞에 출력.
 *
 * [items 작성 팁]
 * - 기본: 일반 문자열. 여러 줄은 `\n` 또는 백틱 줄바꿈 사용 (`white-space: pre-line`).
 * - HTML 태그가 필요할 때: `{ html: "<strong>...</strong>" }` 객체로 넘기면 내부에서 렌더.
 * - 복잡한 JSX(이벤트 핸들러, 컴포넌트)는 ReactNode 그대로 넘기기 (래퍼 요소 권장).
 */

import clsx from 'clsx';
import { useState, type ReactNode } from 'react';
import { CommonInner } from '@publishing/components/ui/CommonInner';
import Icon from '@publishing/components/ui/Icon';
import { ArrowChevron } from '@publishing/components/ui/SvgSprite';
import s from './CommonCautions.module.scss';

/** items 항목 타입: 문자열 · HTML 문자열 객체 · ReactNode */
export type CautionItem = string | { html: string } | ReactNode;

export interface CommonCautionsProps {
  className?: string;
  /** 기본: "유의사항" */
  title?: ReactNode;
  /**
   * 유의사항 문구 목록.
   * - 1개: `<p class="cautions_list">`
   * - 2개 이상: `<ul class="cautions_list"><li>…</li></ul>`
   * - 0개: 본문 영역 미렌더
   */
  items?: CautionItem[];
  /** 목록 영역에 추가할 클래스 */
  itemsClassName?: string;
  /** false면 리스트 스타일 제거, 기본값 true (disc) */
  listStyle?: boolean;
  /** 초기 펼침 여부. false면 마운트 시 접힘 (토글 후 상태는 내부 유지) */
  open?: boolean;
  /** items 외에 추가로 렌더할 자식 요소 */
  children?: ReactNode;
  /** children 렌더 위치. 기본 "after" (items 뒤) */
  childrenPosition?: 'before' | 'after';
}

/** items 항목을 `<p>` / `<li>`에 뿌릴 props로 변환 */
function renderItemProps(
  item: CautionItem,
): { dangerouslySetInnerHTML: { __html: string } } | { children: ReactNode } {
  if (item && typeof item === 'object' && 'html' in item && typeof (item as { html: unknown }).html === 'string') {
    return { dangerouslySetInnerHTML: { __html: (item as { html: string }).html } };
  }
  return { children: item as ReactNode };
}

function getCautionItemKey(item: CautionItem, index: number) {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object' && 'html' in item) return (item as { html: string }).html;
  return `caution-${index}`;
}

export function CommonCautions({
  className,
  title = '유의사항',
  items,
  itemsClassName,
  listStyle = true,
  open: initialOpen = true,
  children,
  childrenPosition = 'after',
}: CommonCautionsProps) {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const n = items?.length ?? 0;
  const listCls = [
    s.cautions_list,
    listStyle === false && s.cautions_list_no_bullet,
    itemsClassName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      className={clsx(s.cautions, className)}
    >
      <CommonInner className={s.cautions_inner}>
        <h2 className={s.cautions_title}>
          <button
            type="button"
            className={s.cautions_title_button}
            aria-expanded={isOpen}
            onClick={() => setIsOpen((v) => !v)}
          >
            {title}
            <span
              className={s.cautions_title_icon}
              data-expanded={isOpen}
              aria-hidden
            >
              <Icon name={ArrowChevron} direction="down" size={24} />
            </span>
          </button>
        </h2>
        {isOpen && (
          <>
            {n > 0 ? (
              <>
                {childrenPosition === 'before' && children}
                {n === 1 ? (
                  <div className={listCls} {...renderItemProps(items![0])} />
                ) : (
                  <ul className={listCls}>
                    {items!.map((item, index) => (
                      <li key={getCautionItemKey(item, index)} {...renderItemProps(item)} />
                    ))}
                  </ul>
                )}
                {childrenPosition === 'after' && children}
              </>
            ) : (
              <div className={listCls}>{children}</div>
            )}
          </>
        )}
      </CommonInner>
    </section>
  );
}
